import React, { useEffect, useState } from 'react';
import {
  GTM_ID,
  readCookieConsent,
  writeCookieConsent,
  type CookieConsentChoice,
} from '../../helpers/cookieConsent';
import styles from './CookieConsent.module.scss';

const GTM_SCRIPT_ID = 'project-secretary-gtm-script';

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown> | unknown[]>;
  }
}

const pushDataLayer = (...args: unknown[]) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
};

const setOptionalCookieConsent = (granted: boolean) => {
  pushDataLayer('consent', 'update', {
    ad_storage: granted ? 'granted' : 'denied',
    analytics_storage: granted ? 'granted' : 'denied',
    ad_user_data: granted ? 'granted' : 'denied',
    ad_personalization: granted ? 'granted' : 'denied',
  });
};

const loadGoogleTagManager = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  pushDataLayer('consent', 'default', {
    ad_storage: 'granted',
    analytics_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
  });

  if (document.getElementById(GTM_SCRIPT_ID)) {
    return;
  }

  window.dataLayer.push({
    event: 'gtm.js',
    'gtm.start': new Date().getTime(),
  });

  const script = document.createElement('script');
  script.id = GTM_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);
};

const disableGoogleTagManager = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  setOptionalCookieConsent(false);
  document.getElementById(GTM_SCRIPT_ID)?.remove();
};

const CookieConsent: React.FC = () => {
  const [choice, setChoice] = useState<CookieConsentChoice | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const stored = readCookieConsent();
    setChoice(stored);
    setIsOpen(!stored);

    if (stored?.optional) {
      loadGoogleTagManager();
    } else {
      setOptionalCookieConsent(false);
    }

    const handleOpenSettings = () => setIsOpen(true);
    window.addEventListener('project-secretary:open-cookie-settings', handleOpenSettings);

    return () => {
      window.removeEventListener('project-secretary:open-cookie-settings', handleOpenSettings);
    };
  }, []);

  const saveChoice = (optional: boolean) => {
    const nextChoice = writeCookieConsent(optional);
    setChoice(nextChoice);
    setIsOpen(false);

    if (optional) {
      loadGoogleTagManager();
    } else {
      disableGoogleTagManager();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.banner} role="dialog" aria-modal="false" aria-label="Cookie consent">
      <div className={styles.copy}>
        <h2>Cookie choices</h2>
        <p>
          Necessary storage keeps the site working. Optional analytics and marketing tags only load if you accept.
        </p>
        {choice && (
          <p className={styles.currentChoice}>
            Current choice: optional cookies {choice.optional ? 'accepted' : 'rejected'}.
          </p>
        )}
      </div>

      <div className={styles.actions}>
        <button type="button" className={styles.secondaryButton} onClick={() => saveChoice(false)}>
          Reject optional cookies
        </button>
        <button type="button" className={styles.primaryButton} onClick={() => saveChoice(true)}>
          Accept optional cookies
        </button>
      </div>
    </div>
  );
};

export default CookieConsent;
